/**
 * Canonical Smart Shikka runtime configuration.
 *
 * This module is intentionally pure: it resolves an already loaded
 * environment snapshot and never reads or writes files, logs values, or
 * contacts a provider. Server launchers and preflight commands must use this
 * resolver instead of independently deriving database, Settings, comms, or
 * Gateway configuration.
 */
const DEFAULT_HTTP_HOST = "127.0.0.1";
const DEFAULT_HTTP_PORT = 37842;
const DEFAULT_SMTP_PORT = 587;
const DEFAULT_SESSION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const LEGACY_ALIASES = {
    SMART_SHIKKA_RUNTIME_ENVIRONMENT: "SMART_SHIKHA_RUNTIME_ENVIRONMENT",
    SMART_SHIKKA_CANONICAL_ENV_FILE: "SMART_SHIKHA_CANONICAL_ENV_FILE",
    SMART_SHIKKA_SERVER_HOST: "SMART_SHIKHA_SERVER_HOST",
    SMART_SHIKKA_SERVER_PORT: "SMART_SHIKHA_SERVER_PORT",
    SMART_SHIKKA_DATABASE_URL: "SMART_SHIKHA_DATABASE_URL",
    SMART_SHIKKA_MIGRATION_DATABASE_URL: "SMART_SHIKHA_MIGRATION_DATABASE_URL",
    SMART_SHIKKA_SETTINGS_STORE_MODE: "SMART_SHIKHA_SETTINGS_STORE_MODE",
    SMART_SHIKKA_SETTINGS_DB_URL: "SMART_SHIKHA_SETTINGS_DB_URL",
    SMART_SHIKKA_DB_PERSISTENCE_ENABLED: "SMART_SHIKHA_DB_PERSISTENCE_ENABLED",
    SMART_SHIKKA_APP_RUNTIME_DB_ROLE: "SMART_SHIKHA_APP_RUNTIME_DB_ROLE",
    SMART_SHIKKA_MIGRATION_DB_ROLE: "SMART_SHIKHA_MIGRATION_DB_ROLE",
    SMART_SHIKKA_WEB_AUTH_ENABLED: "SMART_SHIKHA_WEB_AUTH_ENABLED",
    SMART_SHIKKA_DB_SSL: "SMART_SHIKHA_DB_SSL",
    SMART_SHIKKA_SESSION_TTL_MS: "SMART_SHIKHA_SESSION_TTL_MS",
    SMART_SHIKKA_OTP_TTL_MS: "SMART_SHIKHA_OTP_TTL_MS",
    SMART_SHIKKA_COMMS_SEND_ENABLED: "SMART_SHIKHA_COMMS_SEND_ENABLED",
    SMART_SHIKKA_OTP_DELIVERY_REQUIRED: "SMART_SHIKHA_OTP_DELIVERY_REQUIRED",
    SMART_SHIKKA_OTP_SENDER_MODE: "SMART_SHIKHA_OTP_SENDER_MODE",
    SMART_SHIKKA_COMMS_PROVIDER: "SMART_SHIKHA_COMMS_PROVIDER",
    SMART_SHIKKA_SMTP_HOST: "SMART_SHIKHA_SMTP_HOST",
    SMART_SHIKKA_SMTP_PORT: "SMART_SHIKHA_SMTP_PORT",
    SMART_SHIKKA_SMTP_SECURE: "SMART_SHIKHA_SMTP_SECURE",
    SMART_SHIKKA_SMTP_USER: "SMART_SHIKHA_SMTP_USER",
    SMART_SHIKKA_SMTP_PASS: "SMART_SHIKHA_SMTP_PASS",
    SMART_SHIKKA_SMTP_FROM: "SMART_SHIKHA_SMTP_FROM",
    SMART_SHIKKA_COMMS_EMAIL_URL: "SMART_SHIKHA_COMMS_EMAIL_URL",
    SMART_SHIKKA_COMMS_WHATSAPP_URL: "SMART_SHIKHA_COMMS_WHATSAPP_URL",
    SMART_SHIKKA_COMMS_API_KEY: "SMART_SHIKHA_COMMS_API_KEY",
    SMART_SHIKKA_COMMS_FROM: "SMART_SHIKHA_COMMS_FROM",
    SMART_SHIKKA_COMMS_CONTRACT: "SMART_SHIKHA_COMMS_CONTRACT",
    SMART_SHIKKA_COMMS_TENANT_ID: "SMART_SHIKHA_COMMS_TENANT_ID",
    SMART_SHIKKA_COMMS_ACTOR_ID: "SMART_SHIKHA_COMMS_ACTOR_ID",
    SMART_SHIKKA_GATEWAY_URL: "SMART_SHIKHA_GATEWAY_URL",
    SMART_SHIKKA_GATEWAY_AUTH_TOKEN: "SMART_SHIKHA_GATEWAY_AUTH_TOKEN",
    SMART_SHIKKA_GATEWAY_AUTH_TOKEN_FILE: "SMART_SHIKHA_GATEWAY_AUTH_TOKEN_FILE",
    SMART_SHIKKA_GATEWAY_COMPLETION_PATH: "SMART_SHIKHA_GATEWAY_COMPLETION_PATH",
    SMART_SHIKKA_LLM_OLLAMA_URL: "SMART_SHIKHA_LLM_OLLAMA_URL",
    SMART_SHIKKA_LLM_PROVIDER: "SMART_SHIKHA_LLM_PROVIDER",
    SMART_SHIKKA_LLM_MODEL: "SMART_SHIKHA_LLM_MODEL",
    SMART_SHIKKA_LLM_BASE_URL: "SMART_SHIKHA_LLM_BASE_URL",
    SMART_SHIKKA_LLM_API_KEY: "SMART_SHIKHA_LLM_API_KEY",
    SMART_SHIKKA_LLM_TIMEOUT_MS: "SMART_SHIKHA_LLM_TIMEOUT_MS",
    SMART_SHIKKA_LOCAL_AUTH_TOKEN: "SMART_SHIKHA_LOCAL_AUTH_TOKEN",
    SMART_SHIKKA_SERVER_TOKEN_SCOPES: "SMART_SHIKHA_SERVER_TOKEN_SCOPES",
    SMART_SHIKKA_LLM_KEY_ENCRYPTION_SECRET: "SMART_SHIKHA_LLM_KEY_ENCRYPTION_SECRET",
};
// Keep this list exported so deploy guards and tests can assert that the
// canonical file contains no untracked authority without knowing secret names.
export const CANONICAL_RUNTIME_ENV_KEYS = Object.freeze([
    ...Object.keys(LEGACY_ALIASES),
    "SMART_SHIKKA_TARGET_ENVIRONMENT",
    "SMART_SHIKKA_PUBLIC_URL",
    "SMART_SHIKKA_API_URL",
    "SMART_SHIKKA_FRONTEND_URL",
    "SMART_SHIKKA_RELEASE_VERSION",
    "SMART_SHIKKA_RELEASE_PHASE",
    "SMART_SHIKKA_DEPLOYMENT_MODE",
    "SMART_SHIKKA_JARVIS_ALLOWED_EMAILS",
    "SMART_SHIKKA_TWO_FACTOR_PRIVILEGED",
    "SMART_SHIKKA_OTP_TESTING_PHASE",
    "SMART_SHIKKA_ALLOWED_ORIGINS",
    "SMART_SHIKKA_ALLOWED_PUBLIC_HOSTS",
    "SMART_SHIKKA_REQUEST_TIMEOUT_MS",
    "SMART_SHIKKA_MAX_BODY_BYTES",
    "SMART_SHIKKA_VOICE_REQUEST_TIMEOUT_MS",
    "SMART_SHIKKA_VOICE_MAX_BODY_BYTES",
    "SMART_SHIKKA_MONGODB_URI",
    "SMART_SHIKKA_ARTIFACT_ENCRYPTION_SECRET",
    "SMART_SHIKKA_PROJECTS_BASE",
    "SMART_SHIKKA_HEADROOM_MAX_TOKENS",
    "SMART_SHIKKA_HEADROOM_RESERVE_TOKENS",
    "SMART_SHIKKA_HEADROOM_SYSTEM_MIN_TOKENS",
    "SMART_SHIKKA_HEADROOM_LATEST_TURN_MIN_TOKENS",
    "SMART_SHIKKA_HEADROOM_TOOL_MIN_TOKENS",
    "SMART_SHIKKA_HEADROOM_STRATEGY",
    "SMART_SHIKKA_SMART_PRINT_GATEWAY_ENABLED",
    "SMART_SHIKKA_GATEWAY_AUTH_TOKEN_FILE",
    "SMART_SHIKKA_QWEN_EXTERNAL_FALLBACK_ENABLED",
]);
const FORBIDDEN_DEPLOYED_KEYS = [
    "DATABASE_URL",
    "POSTGRES_URL",
    "POSTGRES_PASSWORD",
    "PGPASSWORD",
    "OLLAMA_BASE_URL",
];
const POSITIVE_BOOLEAN_VALUES = new Set(["true", "1", "yes", "on"]);
const NEGATIVE_BOOLEAN_VALUES = new Set(["false", "0", "no", "off"]);
function nonEmpty(value) {
    const trimmed = value?.trim();
    return trimmed ? trimmed : undefined;
}
function readBoolean(value, fallback, errors, key) {
    const normalized = nonEmpty(value)?.toLowerCase();
    if (!normalized)
        return fallback;
    if (POSITIVE_BOOLEAN_VALUES.has(normalized))
        return true;
    if (NEGATIVE_BOOLEAN_VALUES.has(normalized))
        return false;
    errors.push(`INVALID_BOOLEAN:${key}`);
    return fallback;
}
function readPositiveNumber(value, fallback, errors, key) {
    const normalized = nonEmpty(value);
    if (!normalized)
        return fallback;
    const parsed = Number(normalized);
    if (!Number.isFinite(parsed) || parsed <= 0) {
        errors.push(`INVALID_NUMBER:${key}`);
        return fallback;
    }
    return parsed;
}
function resolveEnvironment(env, errors) {
    const raw = nonEmpty(env.SMART_SHIKKA_RUNTIME_ENVIRONMENT) ?? nonEmpty(env.NODE_ENV);
    const normalized = (raw ?? "development").toLowerCase();
    if (normalized === "production" || normalized === "staging" || normalized === "test" || normalized === "development") {
        return normalized;
    }
    errors.push("INVALID_RUNTIME_ENVIRONMENT");
    return "development";
}
function resolveValue(env, canonicalKey, legacyKeysUsed, duplicateAuthorityKeys, errors, environment) {
    const legacyKey = LEGACY_ALIASES[canonicalKey];
    const canonicalValue = nonEmpty(env[canonicalKey]);
    const legacyValue = legacyKey ? nonEmpty(env[legacyKey]) : undefined;
    if (canonicalValue && legacyValue) {
        duplicateAuthorityKeys.push(`${canonicalKey}:${legacyKey}`);
        if (canonicalValue !== legacyValue)
            errors.push(`CONFLICTING_ENV_KEYS:${canonicalKey}:${legacyKey}`);
        if (environment === "staging" || environment === "production") {
            errors.push(`LEGACY_ENV_FORBIDDEN:${legacyKey}`);
        }
        return canonicalValue;
    }
    if (canonicalValue)
        return canonicalValue;
    if (legacyValue) {
        legacyKeysUsed.push(legacyKey);
        if (environment === "staging" || environment === "production") {
            errors.push(`LEGACY_ENV_FORBIDDEN:${legacyKey}`);
        }
        return legacyValue;
    }
    return undefined;
}
function readUrl(value, key, errors, protocols = ["http:", "https:"]) {
    if (!value)
        return undefined;
    try {
        const parsed = new URL(value);
        if (!protocols.includes(parsed.protocol))
            throw new Error("unsupported protocol");
        if (!parsed.hostname)
            throw new Error("missing host");
        return parsed;
    }
    catch {
        errors.push(`INVALID_URL:${key}`);
        return undefined;
    }
}
function readDatabaseUrl(value, key, errors) {
    return readUrl(value, key, errors, ["postgres:", "postgresql:"]);
}
function classifyTarget(value, parsed) {
    if (!value || !parsed)
        return value ? "invalid" : "unknown";
    const hostname = parsed.hostname.toLowerCase();
    if (hostname === "localhost" || hostname === "::1" || hostname === "127.0.0.1" || hostname.startsWith("127.")) {
        return "local";
    }
    if (hostname.endsWith(".local") ||
        hostname.endsWith(".internal") ||
        hostname.startsWith("10.") ||
        hostname.startsWith("192.168.") ||
        /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)) {
        return "private";
    }
    if (hostname.includes(".") || /^[a-z0-9-]+$/i.test(hostname))
        return "remote";
    return "unknown";
}
function hasTargetMarker(value, marker) {
    return Boolean(value && new RegExp(`(^|[-_.])${marker}([-_.]|$)`, "i").test(value));
}
function normalizeDbUrlForComparison(value) {
    if (!value)
        return undefined;
    try {
        const parsed = new URL(value);
        parsed.password = "[redacted]";
        return parsed.toString();
    }
    catch {
        return undefined;
    }
}
function isSameUrl(left, right) {
    const normalizedLeft = normalizeDbUrlForComparison(left);
    const normalizedRight = normalizeDbUrlForComparison(right);
    return normalizedLeft !== undefined && normalizedLeft === normalizedRight;
}
function hasAny(env, keys) {
    return keys.some((key) => nonEmpty(env[key]) !== undefined);
}
export function resolveSmartShikkaRuntimeConfig(input = process.env) {
    const maybeOptions = input;
    const env = maybeOptions.env !== undefined && typeof maybeOptions.env === "object"
        ? maybeOptions.env
        : input;
    const errors = [];
    const legacyKeysUsed = [];
    const duplicateAuthorityKeys = [];
    const environment = resolveEnvironment(env, errors);
    const get = (key) => resolveValue(env, key, legacyKeysUsed, duplicateAuthorityKeys, errors, environment);
    const canonicalEnvFile = nonEmpty(inputCanonicalEnvFile(input)) ?? get("SMART_SHIKKA_CANONICAL_ENV_FILE");
    const runtimeUrl = get("SMART_SHIKKA_DATABASE_URL");
    const migrationUrl = get("SMART_SHIKKA_MIGRATION_DATABASE_URL");
    const explicitSettingsUrl = get("SMART_SHIKKA_SETTINGS_DB_URL");
    const settingsModeRaw = get("SMART_SHIKKA_SETTINGS_STORE_MODE")?.toLowerCase();
    const settingsMode = settingsModeRaw === "db" ? "db" : "memory";
    if (settingsModeRaw && settingsModeRaw !== "db" && settingsModeRaw !== "memory") {
        errors.push("INVALID_SETTINGS_STORE_MODE");
    }
    const runtimeDb = readDatabaseUrl(runtimeUrl, "SMART_SHIKKA_DATABASE_URL", errors);
    const migrationDb = readDatabaseUrl(migrationUrl, "SMART_SHIKKA_MIGRATION_DATABASE_URL", errors);
    const settingsDb = readDatabaseUrl(explicitSettingsUrl, "SMART_SHIKKA_SETTINGS_DB_URL", errors);
    const settingsUrl = explicitSettingsUrl ?? runtimeUrl;
    const settingsDbDrift = explicitSettingsUrl && runtimeUrl && !isSameUrl(explicitSettingsUrl, runtimeUrl) ? "YES" : "NO";
    if (settingsDbDrift === "YES")
        errors.push("SETTINGS_DB_DRIFT");
    const persistenceEnabled = readBoolean(get("SMART_SHIKKA_DB_PERSISTENCE_ENABLED"), environment === "staging" || environment === "production" ? false : true, errors, "SMART_SHIKKA_DB_PERSISTENCE_ENABLED");
    const runtimeRole = get("SMART_SHIKKA_APP_RUNTIME_DB_ROLE");
    const migrationRole = get("SMART_SHIKKA_MIGRATION_DB_ROLE");
    const roleSeparationValid = Boolean(runtimeUrl && migrationUrl) &&
        !isSameUrl(runtimeUrl, migrationUrl) &&
        runtimeRole === "restricted" &&
        migrationRole === "privileged_migrator";
    const targetEnvironment = get("SMART_SHIKKA_TARGET_ENVIRONMENT");
    if ((environment === "staging" || environment === "production") && targetEnvironment !== environment) {
        errors.push("TARGET_ENVIRONMENT_MISMATCH");
    }
    const noProductionTargetInStaging = environment !== "staging" ||
        (!hasTargetMarker(runtimeUrl, "prod") &&
            !hasTargetMarker(migrationUrl, "prod") &&
            !hasTargetMarker(explicitSettingsUrl, "prod") &&
            !hasTargetMarker(env.SMART_SHIKKA_PUBLIC_URL, "prod"));
    const noStagingTargetInProduction = environment !== "production" ||
        (!hasTargetMarker(runtimeUrl, "stag") &&
            !hasTargetMarker(migrationUrl, "stag") &&
            !hasTargetMarker(explicitSettingsUrl, "stag") &&
            !hasTargetMarker(env.SMART_SHIKKA_PUBLIC_URL, "stag"));
    if (!noProductionTargetInStaging)
        errors.push("PRODUCTION_TARGET_IN_STAGING");
    if (!noStagingTargetInProduction)
        errors.push("STAGING_TARGET_IN_PRODUCTION");
    if (environment === "staging" || environment === "production") {
        if (!runtimeUrl)
            errors.push("MISSING_REQUIRED_KEY:SMART_SHIKKA_DATABASE_URL");
        if (!migrationUrl)
            errors.push("MISSING_REQUIRED_KEY:SMART_SHIKKA_MIGRATION_DATABASE_URL");
        if (!persistenceEnabled)
            errors.push("DB_PERSISTENCE_MUST_BE_ENABLED");
        if (settingsMode !== "db")
            errors.push("SETTINGS_STORE_MODE_MUST_BE_DB");
        if (!settingsUrl)
            errors.push("MISSING_REQUIRED_KEY:SMART_SHIKKA_SETTINGS_DB_URL_DERIVED_FROM_DATABASE_URL");
        if (!roleSeparationValid)
            errors.push("DB_ROLE_SEPARATION_INVALID");
        if (settingsDb === undefined && explicitSettingsUrl)
            errors.push("SETTINGS_DB_URL_INVALID");
    }
    const otpTestingPhase = readBoolean(get("SMART_SHIKKA_OTP_TESTING_PHASE"), false, errors, "SMART_SHIKKA_OTP_TESTING_PHASE");
    if (otpTestingPhase && environment !== "staging") {
        errors.push("OTP_TESTING_PHASE_STAGING_ONLY");
    }
    const sendEnabled = readBoolean(get("SMART_SHIKKA_COMMS_SEND_ENABLED"), false, errors, "SMART_SHIKKA_COMMS_SEND_ENABLED");
    const deliveryRequired = readBoolean(get("SMART_SHIKKA_OTP_DELIVERY_REQUIRED"), otpTestingPhase ? false : environment === "staging" || environment === "production", errors, "SMART_SHIKKA_OTP_DELIVERY_REQUIRED");
    if ((environment === "staging" || environment === "production") && !otpTestingPhase && !deliveryRequired) {
        errors.push("OTP_DELIVERY_MUST_BE_REQUIRED");
    }
    if (otpTestingPhase && deliveryRequired) {
        errors.push("OTP_TESTING_PHASE_DELIVERY_MUST_BE_DISABLED");
    }
    if (otpTestingPhase && sendEnabled) {
        errors.push("OTP_TESTING_PHASE_SEND_MUST_BE_DISABLED");
    }
    const smtpHost = get("SMART_SHIKKA_SMTP_HOST");
    const smtpPort = readPositiveNumber(get("SMART_SHIKKA_SMTP_PORT"), DEFAULT_SMTP_PORT, errors, "SMART_SHIKKA_SMTP_PORT");
    const smtpSecure = readBoolean(get("SMART_SHIKKA_SMTP_SECURE"), false, errors, "SMART_SHIKKA_SMTP_SECURE");
    const smtpUser = get("SMART_SHIKKA_SMTP_USER");
    const smtpPass = get("SMART_SHIKKA_SMTP_PASS");
    const smtpFrom = get("SMART_SHIKKA_SMTP_FROM");
    const smtpConfigured = Boolean(smtpHost && smtpUser && smtpPass && smtpFrom && smtpPort > 0);
    const emailUrl = get("SMART_SHIKKA_COMMS_EMAIL_URL");
    const whatsappUrl = get("SMART_SHIKKA_COMMS_WHATSAPP_URL");
    const commsApiKey = get("SMART_SHIKKA_COMMS_API_KEY");
    const commsFrom = get("SMART_SHIKKA_COMMS_FROM");
    const commsContractRaw = get("SMART_SHIKKA_COMMS_CONTRACT")?.trim().toLowerCase();
    const commsContract = commsContractRaw === "v2" ? "v2" : commsContractRaw === undefined || commsContractRaw === "" || commsContractRaw === "legacy" ? "legacy" : undefined;
    if (commsContract === undefined)
        errors.push("INVALID_COMMS_CONTRACT");
    const commsTenantId = get("SMART_SHIKKA_COMMS_TENANT_ID");
    const commsActorId = get("SMART_SHIKKA_COMMS_ACTOR_ID");
    const httpConfigured = Boolean(commsApiKey && emailUrl);
    const requestedSenderMode = (get("SMART_SHIKKA_OTP_SENDER_MODE") ?? get("SMART_SHIKKA_COMMS_PROVIDER"))?.toLowerCase();
    let senderMode = otpTestingPhase
        ? "dry_run"
        : requestedSenderMode === "dry_run"
            ? "dry_run"
            : requestedSenderMode === "smtp"
                ? "smtp"
                : requestedSenderMode === "http" || requestedSenderMode === "http_comms" || requestedSenderMode === "whatsapp"
                    ? "http_comms"
                    : smtpConfigured
                        ? "smtp"
                        : httpConfigured
                            ? "http_comms"
                            : "dry_run";
    if (requestedSenderMode && !["dry_run", "smtp", "http", "http_comms", "whatsapp"].includes(requestedSenderMode)) {
        errors.push("INVALID_OTP_SENDER_MODE");
        senderMode = "dry_run";
    }
    const smtpProviderValid = senderMode === "smtp" && smtpConfigured;
    const httpProviderValid = senderMode === "http_comms" && httpConfigured;
    // Readiness describes actual provider capability, not whether delivery is
    // currently required. A local dry-run therefore never claims real OTP
    // delivery merely because the requirement is disabled.
    const deliveryReady = sendEnabled && (smtpProviderValid || httpProviderValid) && senderMode !== "dry_run";
    if (deliveryRequired) {
        if (!sendEnabled)
            errors.push("COMMS_SEND_MUST_BE_ENABLED");
        if (senderMode === "dry_run")
            errors.push("OTP_DRY_RUN_FORBIDDEN");
        if (senderMode === "smtp" && !smtpConfigured)
            errors.push("SMTP_PROVIDER_NOT_CONFIGURED");
        if (senderMode === "http_comms" && !httpConfigured)
            errors.push("HTTP_COMMS_PROVIDER_NOT_CONFIGURED");
    }
    const gatewayUrl = get("SMART_SHIKKA_GATEWAY_URL");
    const gatewayAuthToken = get("SMART_SHIKKA_GATEWAY_AUTH_TOKEN");
    const gatewayAuthTokenFile = get("SMART_SHIKKA_GATEWAY_AUTH_TOKEN_FILE");
    const externalFallbackEnabled = readBoolean(get("SMART_SHIKKA_QWEN_EXTERNAL_FALLBACK_ENABLED"), false, errors, "SMART_SHIKKA_QWEN_EXTERNAL_FALLBACK_ENABLED");
    const gatewayParsed = readUrl(gatewayUrl, "SMART_SHIKKA_GATEWAY_URL", errors);
    const gatewayAuthConfigured = Boolean(gatewayAuthToken || gatewayAuthTokenFile);
    const directProviderConfigured = hasAny(env, [
        "OLLAMA_BASE_URL",
        "SMART_SHIKKA_LLM_OLLAMA_URL",
        "SMART_SHIKHA_LLM_OLLAMA_URL",
        "SMART_SHIKKA_LLM_BASE_URL",
        "SMART_SHIKHA_LLM_BASE_URL",
        "SMART_SHIKKA_LLM_API_KEY",
        "SMART_SHIKHA_LLM_API_KEY",
    ]);
    const gatewayReady = !gatewayUrl || (gatewayParsed !== undefined && gatewayAuthConfigured);
    if ((environment === "staging" || environment === "production") && directProviderConfigured) {
        errors.push("DIRECT_PROVIDER_URL_FORBIDDEN");
    }
    if ((environment === "staging" || environment === "production") && externalFallbackEnabled) {
        errors.push("EXTERNAL_PROVIDER_FALLBACK_FORBIDDEN");
    }
    if (gatewayUrl && !gatewayParsed)
        errors.push("GATEWAY_CONFIG_INVALID");
    if (gatewayUrl && !gatewayAuthConfigured)
        errors.push("GATEWAY_AUTH_NOT_CONFIGURED");
    const httpPort = readPositiveNumber(get("SMART_SHIKKA_SERVER_PORT"), DEFAULT_HTTP_PORT, errors, "SMART_SHIKKA_SERVER_PORT");
    const httpHost = get("SMART_SHIKKA_SERVER_HOST") ?? DEFAULT_HTTP_HOST;
    const sessionTtlMs = readPositiveNumber(get("SMART_SHIKKA_SESSION_TTL_MS"), DEFAULT_SESSION_TTL_MS, errors, "SMART_SHIKKA_SESSION_TTL_MS");
    const localAuthToken = nonEmpty(env.SMART_SHIKKA_LOCAL_AUTH_TOKEN) ?? nonEmpty(env.SMART_SHIKHA_LOCAL_AUTH_TOKEN);
    const tokenScopes = get("SMART_SHIKKA_SERVER_TOKEN_SCOPES");
    for (const key of FORBIDDEN_DEPLOYED_KEYS) {
        if ((environment === "staging" || environment === "production") && nonEmpty(env[key])) {
            errors.push(`FORBIDDEN_ENV_KEY:${key}`);
        }
    }
    if ((environment === "staging" || environment === "production") && duplicateAuthorityKeys.length > 0) {
        errors.push("DUPLICATE_ENV_AUTHORITY");
    }
    const forbiddenKeys = FORBIDDEN_DEPLOYED_KEYS.filter((key) => nonEmpty(env[key]) !== undefined);
    const uniqueErrors = [...new Set(errors)];
    return {
        environment,
        canonicalEnvFile,
        database: {
            runtimeUrl,
            migrationUrl,
            settingsUrl,
            persistenceEnabled,
            runtimeRole,
            migrationRole,
            runtimeTargetClass: classifyTarget(runtimeUrl, runtimeDb),
            migrationTargetClass: classifyTarget(migrationUrl, migrationDb),
            roleSeparationValid,
            settingsDbDrift,
        },
        settings: {
            storeMode: settingsMode,
            dbUrl: settingsUrl,
            persistenceReady: settingsMode === "db" &&
                persistenceEnabled &&
                Boolean(runtimeDb && settingsUrl && settingsDbDrift === "NO"),
        },
        comms: {
            sendEnabled,
            deliveryRequired,
            otpTestingPhase,
            senderMode,
            smtpConfigured,
            httpConfigured,
            deliveryReady,
            smtp: {
                host: smtpHost,
                port: smtpPort,
                secure: smtpSecure,
                user: smtpUser,
                pass: smtpPass,
                from: smtpFrom,
            },
            http: {
                emailUrl,
                whatsappUrl,
                apiKey: commsApiKey,
                from: commsFrom,
                contract: commsContract ?? "legacy",
                tenantId: commsTenantId,
                actorId: commsActorId,
            },
        },
        gateway: {
            url: gatewayUrl,
            authToken: gatewayAuthToken,
            authTokenFile: gatewayAuthTokenFile,
            authConfigured: gatewayAuthConfigured,
            ready: gatewayReady,
            directProviderConfigured,
        },
        http: {
            host: httpHost,
            port: httpPort,
        },
        session: {
            localAuthToken,
            tokenScopes,
            ttlMs: sessionTtlMs,
        },
        validationErrors: uniqueErrors,
        legacyKeysUsed: [...new Set(legacyKeysUsed)],
        duplicateAuthorityKeys: [...new Set(duplicateAuthorityKeys)],
        forbiddenKeys,
    };
}
function inputCanonicalEnvFile(input) {
    if ("canonicalEnvFile" in input && input.canonicalEnvFile !== undefined)
        return nonEmpty(input.canonicalEnvFile);
    return undefined;
}
export function assertSmartShikkaRuntimeConfig(config) {
    if (config.validationErrors.length > 0) {
        throw new Error(`RUNTIME_CONFIG_NOT_READY:${config.validationErrors.join(",")}`);
    }
}
export function createSmartShikkaRuntimeConfigStatus(config, canonicalEnvFileFound = true) {
    const requiredKeysPresent = config.environment === "development" ||
        config.environment === "test" ||
        (Boolean(config.database.runtimeUrl) &&
            Boolean(config.database.migrationUrl) &&
            config.database.persistenceEnabled &&
            config.settings.storeMode === "db" &&
            Boolean(config.settings.dbUrl));
    const noProductionTargetInStaging = !config.validationErrors.includes("PRODUCTION_TARGET_IN_STAGING");
    const noStagingTargetInProduction = !config.validationErrors.includes("STAGING_TARGET_IN_PRODUCTION");
    const status = {
        environment: config.environment,
        canonicalEnvFileFound,
        requiredKeysPresent,
        noDuplicateAuthority: config.duplicateAuthorityKeys.length === 0,
        dbRuntimeTargetClass: config.database.runtimeTargetClass,
        dbMigrationTargetClass: config.database.migrationTargetClass,
        dbRoleSeparationValid: config.database.roleSeparationValid,
        settingsBackendReady: config.environment === "development" ||
            config.environment === "test" ||
            config.settings.persistenceReady,
        settingsDbDrift: config.database.settingsDbDrift,
        commsSendEnabledExpectationValid: config.comms.otpTestingPhase
            ? !config.comms.sendEnabled
            : !config.comms.deliveryRequired || config.comms.sendEnabled,
        smtpOrProviderConfigured: !config.comms.deliveryRequired || config.comms.smtpConfigured || config.comms.httpConfigured,
        otpSenderModeValid: config.comms.otpTestingPhase
            ? config.comms.senderMode === "dry_run"
            : !config.comms.deliveryRequired || config.comms.senderMode !== "dry_run",
        otpDeliveryReady: config.comms.deliveryReady,
        gatewayConfigValid: config.gateway.ready && !config.gateway.directProviderConfigured,
        noProductionTargetInStaging,
        noStagingTargetInProduction,
        valid: (config.environment === "development" ||
            config.environment === "test" ||
            canonicalEnvFileFound) &&
            requiredKeysPresent &&
            config.validationErrors.length === 0,
    };
    return status;
}
//# sourceMappingURL=runtimeConfig.js.map